import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ["triggerInput", "conditionalElement"]
  static values = {
    animate: { type: Boolean, default: true },
  }

  connect() {
    // Initial visibility check of all inputs when controller connects. Loops through all inputs to check its value and toggle the any related elements if neccesary
    this.triggerInputTargets.forEach(input => {
      this.toggleConditionalElements(input, true)
    })
    this.addTriggerElementsEventListeners();
  }
  
  toggleConditionalElements(input, isInitialCheck){
    if(input.id != null && input.id.trim() !== ""){
      //These all are the elements (inputs or other nodes) that have a condition based on the triggered input
      const conditionalElements = []
      this.conditionalElementTargets.forEach(element => {
        if(element.dataset.formFlowDependsOnTriggerInputs.includes(input.id)){
          conditionalElements.push(element);
        }
      })

      const conditionalElementsConditions = this.getConditions(conditionalElements); //We get their conditions to be shown

      conditionalElements.forEach((element, index) => {
        const areConditionsMet = this.checkConditions(conditionalElementsConditions[index]); //We check if all the necessary conditions are met
        this.toggleShow(element, areConditionsMet, !isInitialCheck);
        // this.toggleEnable(element, areConditionsMet, !isInitialCheck);
      });
    }
  }

  getConditions(elements){
    const relatedElementConditions = [];

    elements.length && elements.forEach(element => {
      const thisElementConditions = [];
      if(element.dataset.formFlowDependsOnTriggerInputs?.trim() !== "" && element.dataset.formFlowDependsOnTriggerInputsResults?.trim() !== ""){
        const allTriggerElements = element.dataset.formFlowDependsOnTriggerInputs?.trim();
        const allTriggerResults = element.dataset.formFlowDependsOnTriggerInputsResults?.trim();
        
        const triggerElements = allTriggerElements ? allTriggerElements.split(';').flatMap(elements => elements.includes(',') ? elements.split(',') : [elements]) : [];
        const triggerResults = allTriggerResults ? allTriggerResults.split(';').map(results => results.includes(',') ? results.split(',') : [results]) : [];

        if(triggerElements.length && triggerResults.length){
          // Map each triggerElement and triggerResults to an object with its corresponding values
          triggerElements.forEach((element, index) => {
            thisElementConditions.push({ input: element, objectiveValue: triggerResults[index]});
          });

          relatedElementConditions.push(thisElementConditions);
        }
        else{
          console.error(`Form Flow Controller ERROR: Mismatched amout of elements and results in ${element.id}`);
        }
      }
    });

    return relatedElementConditions;
  }

  checkConditions(conditions){
    return conditions.every(condition => {
      const triggerInput = { input: condition.input, value: this.getCleanInputValue(document.getElementById(condition.input))}
    
      const containsAny = triggerInput.value.length && condition.objectiveValue.some(element => triggerInput.value.includes(element));
      const containSameElements = condition.objectiveValue.length === triggerInput.value.length && condition.objectiveValue.every(value => triggerInput.value.includes(value));

      return (triggerInput.input === condition.input && (containsAny || containSameElements));
    });
  }

  addTriggerElementsEventListeners(){
    this.triggerInputTargets.length && this.triggerInputTargets.forEach(input => {
      input.addEventListener('input', (event) => this.toggleConditionalElements(event.target, false));
    });
  }
  
  async toggleShow(element, shouldShowElement, shouldAnimateElement){
    const isElementInput = ['INPUT', 'SELECT', 'TEXTAREA', 'BUTTON'].includes(element.tagName);
    const shouldDisableInput = element.dataset.formFlowDisableWhenHidden?.trim() !== "" && element.dataset.formFlowDisableWhenHidden == 'true';

    if (isElementInput && shouldDisableInput) {
      element.disabled = !shouldShowElement;
    }

    //If the element to show/hide is an input we need to focus on the parent DIV instead (so we also hide and show the label and other related stuff). We apply assign the parent DIV as the element and move the 'hide' class to it if necessary
    if(isElementInput){
      if(element.classList.contains('hide')){
        element.parentElement.classList.add('hide');
        element.classList.remove('hide');
      }
      element = element.parentElement;
    }

    element.classList.add('form-flow-conditional-element');

    if(this.hasAnimateValue && this.animateValue){
      if(shouldShowElement && element.classList.contains('hide')){
        const endHeight = await this.getHiddenElementHeight(element) + 'px';
        
        element.style.opacity = 0;
        element.style.transform = 'translateY(-15px)';
        shouldAnimateElement ? element.classList.replace('hide', 'animate') : element.classList.remove('hide');
        setTimeout(() => {
          Object.assign(element.style, { transform: 'translateY(0px)', opacity: 1, height: endHeight, overflow: 'visible'});
        }, 25);
      }
      else if(!shouldShowElement && !element.classList.contains('hide')) {
        element.classList.add('animate');
        Object.assign(element.style, { transform: 'translateY(-15px)', opacity: 0, height: '0px', overflow: 'hidden'});
        setTimeout(() => {
          element.classList.replace('animate', 'hide')
        }, 400);
      }
    }
    else{
      element.classList.toggle('hide', !shouldShowElement);
    }
  }

  async getHiddenElementHeight(element){
    const clone = element.cloneNode(true);

    clone.classList.remove('hide');
    Object.assign(clone.style, { display: 'block !important', position: 'absolute', top: '-9999px', left: '-9999px', opacity: '0', height: 'auto' });
    
    document.body.appendChild(clone);

    return clone.offsetHeight; // Or clone.getBoundingClientRect().height;
  }

  getCleanInputValue(input){
    switch(input.type) {
      case 'checkbox':
        return input.checked ? ['checked'] : ['unchecked'];    
      case 'radio':
        return input.checked ? ['checked'] : ['unchecked'];
      case 'select-one':
        if (input.choices) {
          const selectedChoice = input.choices.getValue(true);
          return selectedChoice ? [selectedChoice] : [];
        }
        return input.value ? [input.value] : [];
      case 'select-multiple':
        if (input.choices) {
          const selectedOptions = input.choices.getValue();
          return selectedOptions.map(option => option.value);
        }
        return Array.from(input.selectedOptions).map(option => option.value);
      default:
        return [input.value] || [];
    }
  }

  getCleanConditionValue(value){
    const normalizedValue = value.toLowerCase()
    
    switch (normalizedValue) {
      case 'true':
          return true
      case 'false':
          return false
      default:
          return normalizedValue
    }
  }
}